// NotificationPage.js
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Typography,
  List,
  Avatar,
  Tag,
  Empty,
  Button,
  Modal,
  Popconfirm,
  Spin,
  Alert,
  message as antdMessage,
  Descriptions, // **** IMPORT ADDED: Row, Col, Descriptions ****
} from "antd";
import {
  BellOutlined,
  ClockCircleOutlined,
  UserOutlined,
  SolutionOutlined,
  EyeOutlined,
  DeleteOutlined,
  CheckOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from "../../services/notificationService";

// Destructure Typography components for cleaner usage
const { Title, Text, Paragraph } = Typography;

// Consistent Icon function (reuse)
const getNotificationIcon = (type) => {
  let props = { style: {} }; // Base props
  if (type === "quote_received") {
    props.icon = <SolutionOutlined />;
    props.style.backgroundColor = "#1890ff"; // Blue for quote received
  } else if (type === "user_added") {
    props.icon = <UserOutlined />;
    props.style.backgroundColor = "#52c41a"; // Green for user added
  } else if (type === "quote_status_change") {
    props.icon = <BellOutlined />; // Keeping Bell for status change, could use CheckCircleOutlined etc.
    props.style.backgroundColor = "#faad14"; // Orange for status change
  } else {
    props.icon = <BellOutlined />; // Default
    props.style.backgroundColor = "#bfbfbf"; // Grey for default
  }
  // Return Avatar component directly
  return <Avatar {...props} />;
};

const NotificationPage = () => {
  // ... (existing state: notifications, loading, error, isModalVisible, selectedNotification, actionLoading) ...
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // --- Callbacks for Handlers (memoized) ---
  const handleMarkSingleRead = useCallback(
    async (id) => {
      const notification = notifications.find((n) => n.id === id);
      if (notification && !notification.read) {
        try {
          await markNotificationRead(id);
          setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, read: true } : n))
          );
        } catch (err) {
          console.error("Failed to mark notification as read:", err);
          antdMessage.error("Failed to mark notification as read.");
        }
      }
    },
    [notifications]
  );

  const handleViewNotification = useCallback(
    (item) => {
      setSelectedNotification(item);
      setIsModalVisible(true);
      if (item && !item.read) {
        handleMarkSingleRead(item.id);
      }
    },
    [handleMarkSingleRead]
  );

  const fetchNotificationsData = useCallback(async () => {
    // ... (fetch logic remains the same) ...
    setLoading(true);
    setError(null);
    try {
      const fetchedNotifications = await getNotifications();
      setNotifications(fetchedNotifications);
      return fetchedNotifications;
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      setError("Could not load notifications. Please try again later.");
      antdMessage.error("Failed to load notifications.");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // --- UseEffects (remain the same) ---
  useEffect(() => {
    fetchNotificationsData();
  }, [fetchNotificationsData]);

  useEffect(() => {
    if (notifications.length > 0) {
      const searchParams = new URLSearchParams(location.search);
      const viewId = searchParams.get("view");
      if (viewId) {
        const notificationToView = notifications.find((n) => n.id === viewId);
        if (notificationToView) {
          handleViewNotification(notificationToView);
          // Optional: navigate(location.pathname, { replace: true });
        } else {
          // Optional: navigate(location.pathname, { replace: true });
        }
      }
    }
  }, [
    location.search,
    notifications,
    handleViewNotification,
    navigate,
    location.pathname,
  ]);

  // --- Other Handlers (delete, mark all, modal cancel - remain the same) ---
  const handleDeleteNotification = async (id) => {
    setActionLoading(true);
    try {
      await deleteNotification(id);
      antdMessage.success("Notification deleted successfully");
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (selectedNotification?.id === id) {
        handleModalCancel();
      }
    } catch (err) {
      console.error("Failed to delete notification:", err);
      antdMessage.error("Failed to delete notification.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    setActionLoading(true);
    try {
      const result = await markAllNotificationsRead();
      if (result.updated) {
        antdMessage.success("All notifications marked as read");
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      } else {
        antdMessage.info("No unread notifications to mark.");
      }
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
      antdMessage.error("Failed to mark all notifications as read.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedNotification(null);
    // Optional: Clear query param
    // const searchParams = new URLSearchParams(location.search);
    // if (searchParams.has('view')) {
    //    navigate(location.pathname, { replace: true });
    // }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  // --- Render Logic ---
  if (loading && notifications.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <div>
      {/* Header Area (Title, Mark All Button) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px" /* Increased margin */,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <BellOutlined style={{ marginRight: 8 }} /> Notifications (
          {notifications.length})
        </Title>
        <Button
          icon={<CheckSquareOutlined />}
          onClick={handleMarkAllRead}
          disabled={unreadCount === 0 || actionLoading}
          loading={actionLoading && unreadCount > 0}
        >
          Mark all as read ({unreadCount})
        </Button>
      </div>

      {/* Notification List */}
      <List
        itemLayout="horizontal"
        dataSource={notifications}
        loading={loading && notifications.length > 0}
        renderItem={(item) => (
          <List.Item
            style={{
              backgroundColor: item.read ? "#fff" : "#e6f7ff",
              padding: "12px 16px",
              borderRadius: "4px",
              marginBottom: "10px", // Slightly increased margin
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)", // Slightly stronger shadow
            }}
            actions={[
              // Actions moved slightly closer if needed via style or keep as is
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => handleViewNotification(item)}
                title="View Details"
              />,
              <Popconfirm
                title="Delete this notification?"
                onConfirm={() => handleDeleteNotification(item.id)}
                okText="Yes"
                cancelText="No"
                disabled={actionLoading}
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={actionLoading}
                  title="Delete"
                />
              </Popconfirm>,
              !item.read ? (
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  onClick={() => handleMarkSingleRead(item.id)}
                  title="Mark as Read"
                  style={{ color: "#1890ff" }}
                />
              ) : null,
            ]}
          >
            <List.Item.Meta
              avatar={getNotificationIcon(item.type)} // Use updated function
              title={
                <Text
                  strong={!item.read}
                  style={{ fontSize: "1.05em" /* Slightly larger title */ }}
                >
                  {item.message}
                </Text>
              }
              description={
                <Text type="secondary" style={{ fontSize: "0.9em" }}>
                  <ClockCircleOutlined style={{ marginRight: 4 }} />
                  {new Date(item.timestamp).toLocaleString()}
                </Text>
              }
            />
            {!item.read && (
              <Tag
                color="blue"
                style={{ alignSelf: "center", marginLeft: "10px" }}
              >
                New
              </Tag>
            )}
          </List.Item>
        )}
        locale={{ emptyText: <Empty description="No notifications found." /> }}
      />

      {/* View Notification Modal - UPDATED DESIGN */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            {/* Reuse icon logic in title */}
            {selectedNotification &&
              React.cloneElement(
                getNotificationIcon(selectedNotification.type),
                {
                  size: "small",
                  style: {
                    ...getNotificationIcon(selectedNotification.type).props
                      .style,
                    marginRight: 10,
                  },
                }
              )}
            <Text strong>Notification Details</Text>
          </div>
        }
        open={isModalVisible}
        onCancel={handleModalCancel}
        footer={[
          <Button key="back" onClick={handleModalCancel}>
            Close
          </Button>,
        ]}
        width={600} // Wider modal for better layout
        destroyOnClose // Ensures state is reset if needed
      >
        {selectedNotification && (
          <Descriptions bordered column={1} size="small" layout="horizontal">
            {/* Main Message */}
            <Descriptions.Item
              label="Message"
              labelStyle={{ width: "100px" /* Adjust label width */ }}
            >
              <Text strong>{selectedNotification.message}</Text>
            </Descriptions.Item>

            {/* Timestamp */}
            <Descriptions.Item label="Received" labelStyle={{ width: "100px" }}>
              {new Date(selectedNotification.timestamp).toLocaleString()}
            </Descriptions.Item>

            {/* Status */}
            <Descriptions.Item label="Status" labelStyle={{ width: "100px" }}>
              <Tag
                color={
                  selectedNotification.read ? "default" : "processing"
                } /* Use 'processing' for subtle pulse */
              >
                {selectedNotification.read ? "Read" : "Unread"}
              </Tag>
            </Descriptions.Item>

            {/* Details */}
            <Descriptions.Item label="Details" labelStyle={{ width: "100px" }}>
              {selectedNotification.details ? (
                // Use Paragraph to allow for potential line breaks in details
                <Paragraph style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                  {selectedNotification.details}
                </Paragraph>
              ) : (
                <Text type="secondary">N/A</Text> // Indicate if no details provided
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default NotificationPage;
